
import { supabase } from '@/integrations/supabase/client';
import { AnonymousUserService } from './AnonymousUserService';
import type { Prayer } from '@/types/prayer';

const ABIEL_SYSTEM_ID = '00000000-0000-0000-0000-000000000001';

export class EnhancedPrayerService {
  // Create AI prayer request for both anonymous and authenticated users
  static async createAIPrayerRequest(requestData: {
    request_text: string;
    subject?: string;
    selected_tags?: string[];
    include_bible_verse?: boolean;
  }, userId?: string) {
    const sessionId = AnonymousUserService.getSessionId();
    
    const requestPayload = {
      ...requestData,
      user_id: userId || null,
      session_id: sessionId,
      status: 'pending'
    };

    // For anonymous users, also store locally as backup
    if (!userId) {
      AnonymousUserService.storePrayerRequest(requestPayload);
    }

    const { data, error } = await supabase
      .from('ai_prayer_requests')
      .insert([requestPayload])
      .select()
      .single();

    if (error) {
      console.error('Error creating AI prayer request:', error);
      throw error;
    }

    return data;
  }

  // Save generated prayer with dual-save for authenticated users
  static async saveGeneratedPrayer(
    prayerContent: string,
    originalRequest: any,
    userId?: string
  ): Promise<{ userPrayer?: Prayer; abielPrayer?: Prayer }> {
    const result: { userPrayer?: Prayer; abielPrayer?: Prayer } = {};

    // For authenticated users, save to both user's prayers and Abiel's "prayed for" collection
    if (userId) {
      // Save to user's private prayers
      const userPrayerData = {
        title: originalRequest.subject || 'AI Generated Prayer',
        content: prayerContent,
        author_id: userId,
        author_name: 'User', // Will be populated by profile join
        category: 'AI Generated',
        status: 'active',
        visibility: 'private',
        generated_for_user_id: userId
      };

      const { data: userPrayer, error: userError } = await supabase
        .from('prayers')
        .insert([userPrayerData])
        .select()
        .single();

      if (userError) {
        console.error('Error saving user prayer:', userError);
      } else {
        result.userPrayer = this.mapSupabaseRowToPrayer(userPrayer);
      }

      // Save to Abiel's "prayed for" collection
      const abielPrayerData = {
        title: `Prayer for ${originalRequest.subject || 'User Request'}`,
        content: prayerContent,
        author_id: ABIEL_SYSTEM_ID,
        author_name: 'Abiel AI',
        category: 'Prayed For',
        status: 'active',
        visibility: 'public',
        generated_for_user_id: userId
      };

      const { data: abielPrayer, error: abielError } = await supabase
        .from('prayers')
        .insert([abielPrayerData])
        .select()
        .single();

      if (abielError) {
        console.error('Error saving Abiel prayer:', abielError);
      } else {
        result.abielPrayer = this.mapSupabaseRowToPrayer(abielPrayer);
      }
    }

    return result;
  }

  // Record prayer choice action for both anonymous and authenticated users
  static async recordPrayerChoiceAction(
    prayerId: string,
    actionType: 'i_need_prayer' | 'i_want_to_pray',
    choiceMethod: 'ai_assistant' | 'manual_prayer',
    userId?: string
  ) {
    const sessionId = AnonymousUserService.getSessionId();
    
    const actionData = {
      prayer_id: prayerId,
      action_type: actionType,
      choice_method: choiceMethod,
      user_id: userId || null,
      metadata: {
        session_id: sessionId,
        timestamp: new Date().toISOString(),
        user_type: userId ? 'authenticated' : 'anonymous'
      }
    };

    const { data, error } = await supabase
      .from('prayer_choice_actions')
      .insert([actionData])
      .select()
      .single();

    if (error) {
      console.error('Error recording prayer choice action:', error);
      throw error;
    }

    return data;
  }

  // Get Abiel's "prayed for" prayers
  static async getAbielPrayedForPrayers(limit = 20, offset = 0) {
    const { data, error } = await supabase
      .from('prayers')
      .select('*')
      .eq('author_id', ABIEL_SYSTEM_ID)
      .eq('category', 'Prayed For')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Error fetching Abiel prayed for prayers:', error);
      throw error;
    }

    return data?.map(this.mapSupabaseRowToPrayer) || [];
  }

  // Utility to map Supabase row to Prayer type
  private static mapSupabaseRowToPrayer(row: any): Prayer {
    return {
      id: row.id,
      title: row.title || '',
      content: row.content || '',
      author: row.author_name || '',
      date: row.created_at || new Date().toISOString(),
      category: row.category || 'general',
      status: row.status || 'active',
      visibility: row.visibility || 'public',
      group: row.circle_id || null,
      isFavorite: false,
      isBookmarked: false,
      audioStoragePath: undefined,
      authorAvatarStoragePath: undefined,
    };
  }
}
